import React, { useEffect, useRef, useState } from 'react'
import { Canvas } from 'fabric'
import { message, notification, Spin } from '@pankod/refine-antd'
import { AligningGuidelines } from 'fabric/extensions'

import {
  deleteObject,
  exportSVG,
  createCanvasOnTab,
  fontLoader,
  moveActiveObjectUp,
  moveActiveObjectDown,
  generateSVGPreview,
  clearAllSelection,
  getSVGStringFromUrl,
  getObjByKey,
  createCanvas,
  loadSVGStringIntoCanvas,
} from './utils/canvas'
import { addTextbox } from './utils/textbox'
import useHotKey from './utils/hooks'
import { addImage } from './utils/image'
import { CanvasFooter } from './components/CanvasFooter'
import { useCanvasHistoryStack } from './hooks/useCanvasHistoryStack'
import { useCutCopyPaste } from './hooks/useCutCopyPaste'
import { PageRadio } from './components/PageRadio'
import {
  CANVAS_CONFIG,
  CANVAS_TAB_ID,
  TTE_PLACEHOLDER_KEY,
  QR_CODE_CERTIFICATE_KEY,
} from './constants'
import { OrderNavigator } from './components/OrderNavigator'
import SelectedComponentConfig from './components/SelectedComponentConfig'
import { ButtonSVGPreview } from './components/ModalPreviewSVG'
import MainToolbar from './components/MainToolbar'
import {
  CertificateTemplate,
  CertificateTemplateVariables,
  CertificateTemplatePayload,
  UserVariable,
  RAWCertificateTemplatePayload,
} from '../types'
import { certificateTabChangeEvent } from './utils/events'
import { exportObjectToSVG } from './utils/exportSVG'
import useCanvasTabObject from './hooks/useCanvasTabObject'
import { useWarnIfUnsavedChanges } from 'utils/hooks'
import {
  ensureBsreStampOnCanvas,
  isBsreStampObject,
  findBsreStamp,
} from './utils/bsreStamp'

type FabricWrapper = {
  templates?: CertificateTemplate
  variables?: CertificateTemplateVariables
  onCreateVariable: (variable: UserVariable) => Promise<any>
  onDeleteVariable: (key: string) => Promise<any>
  onPublish: () => Promise<any>
  onSave: (payload: CertificateTemplatePayload) => Promise<any>
  onExport: (payload: CertificateTemplatePayload) => Promise<any>
  loading?: boolean
  navigatorLoading?: boolean
  publishLabel?: string
  requiredTtePlaceholderCount?: number
  skipUnsavedChangesGuard?: boolean
}

const FabricWrapper = (props: FabricWrapper) => {
  const { canvas, canvasTabObject, activeTab, setActiveTab, updateTabObject } =
    useCanvasTabObject()
  const [isLoading, setLoading] = useState(false)
  const canvasModified = useRef([false, false])
  const [canvasModifiedState, setCanvasModifiedState] = useState([false, false])
  const bsreAutoModified = useRef([false, false])
  const isInitializingTemplate = useRef(false)
  const [isCanvasReady, setIsCanvasReady] = useState(false)

  const setCanvasModified = (tabIndex: number, value: boolean) => {
    if (canvasModified.current[tabIndex] !== value) {
      canvasModified.current[tabIndex] = value
      setCanvasModifiedState([...canvasModified.current])
    }
  }

  const [initialMount, setInitialMount] = useState<boolean>(true)
  const [shouldLoadInitialTemplate, setShouldLoadInitialTemplate] = useState({
    front: true,
    back: true,
  })

  const [tabObjToDelete, setTabObjToDelete] = useState<any[]>([])

  const container = useRef<HTMLDivElement>(null)
  const {
    undo,
    redo,
    saveState,
    updateTabHistoryStack,
    currentStackObj,
    isProcessingHistory,
    stackCursor,
  } = useCanvasHistoryStack(canvas.current)
  const { cut, copy, paste, duplicate } = useCutCopyPaste(canvas.current)

  const cleanCursor = useRef([0, 0])
  useWarnIfUnsavedChanges(canvasModified.current.some((modified) => modified))
  useEffect(() => {
    const fetchFont = async () => {
      try {
        setLoading(true)
        await fontLoader()
      } catch {
        message.error('Error loading font')
      } finally {
        setLoading(false)
      }
    }

    const handleCanvasModified = (e?: any) => {
      if (isInitializingTemplate.current || isProcessingHistory.current) return
      if (e?.target && isBsreStampObject(e.target)) return
      updateTabObject()
      saveState(canvas.current, activeTab)
      if (!canvasModified.current[activeTab]) {
        setCanvasModified(activeTab, true)
      }
    }

    const addObjectListeners = () => {
      // hacky way to handle first added object on initial template
      if (
        ((activeTab === 1 && props.templates?.backTemplateUrl !== '') ||
          (activeTab === 0 && props.templates?.frontTemplateUrl !== '')) &&
        !bsreAutoModified.current[activeTab]
      ) {
        setCanvasModified(activeTab, false)
      }
      canvas.current?.on('object:modified', handleCanvasModified)
      canvas.current?.on('object:added', handleCanvasModified)
      canvas.current?.on('object:removed', handleCanvasModified)
    }

    const removeObjectListeners = () => {
      canvas.current?.off('object:modified', handleCanvasModified)
      canvas.current?.off('object:added', handleCanvasModified)
      canvas.current?.off('object:removed', handleCanvasModified)
    }

    if (initialMount) {
      setInitialMount(false)
      setActiveTab(0)
      fetchFont()
    } else {
      canvas.current = createCanvasOnTab({
        canvasTabObject,
        activeTab,
        shouldSaveOnObjectInit:
          shouldLoadInitialTemplate.back || shouldLoadInitialTemplate.front,
      })

      new AligningGuidelines(canvas.current, CANVAS_CONFIG.alignGuideline)
      setIsCanvasReady(true)

      // FORCE REMOVE DELETE STACK
      setTimeout(async () => {
        await deleteCustomObjectFromStackOnTab(activeTab, null, canvas.current)
        const isWaitingForTemplate =
          activeTab === 0
            ? shouldLoadInitialTemplate.front
            : shouldLoadInitialTemplate.back

        if (!isWaitingForTemplate) {
          const wasStampModified = await ensureBsreStampOnCanvas(
            canvas.current,
            props.templates?.isUsingTte,
          )
          if (wasStampModified) {
            bsreAutoModified.current[activeTab] = true
            setCanvasModified(activeTab, true)
          } else {
            // If no system modifications, base load is the clean state
            cleanCursor.current[activeTab] = stackCursor
          }
        }
      }, 0)
    }

    const handleAfterRender = async () => {
      if (isInitializingTemplate.current) return

      const currentObjectsCount = canvas.current?.getObjects().length || 0
      const savedObjectsCount =
        canvasTabObject[activeTab]?.canvasObj.objects.length

      if (currentObjectsCount !== savedObjectsCount) {
        const wasStampModified = await ensureBsreStampOnCanvas(
          canvas.current,
          props.templates?.isUsingTte,
        )
        if (wasStampModified) {
          bsreAutoModified.current[activeTab] = true
          setCanvasModified(activeTab, true)
        }
        await updateTabObject()
        saveState(canvas.current, activeTab)

        if (!wasStampModified && !bsreAutoModified.current[activeTab]) {
          // If no system interventions occurred, this is a clean state
          cleanCursor.current[activeTab] = stackCursor + 1
        }
      } else {
        cleanCursor.current[activeTab] = stackCursor
      }

      canvas.current?.off('after:render', handleAfterRender)
      addObjectListeners()
    }
    canvas.current?.on('after:render', handleAfterRender)
    // hacky way to handle first added object on initial template
    canvas.current?.on('object:added', (e: any) => {
      if (isInitializingTemplate.current || isProcessingHistory.current) return
      if (!canvasModified.current[activeTab]) {
        setCanvasModified(activeTab, true)
      }
      if (!isBsreStampObject(e.target)) {
        const stamp = findBsreStamp(canvas.current)
        if (stamp) canvas.current?.bringObjectToFront(stamp)
      }
    })
    return () => {
      removeObjectListeners()
      canvas.current?.off('after:render', handleAfterRender)
      canvas.current?.dispose()
      canvas.current = null
      setIsCanvasReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canvas, props.templates])

  useEffect(() => {
    const { loading } = props

    if (
      !loading &&
      isCanvasReady &&
      (shouldLoadInitialTemplate.back || shouldLoadInitialTemplate.front)
    ) {
      initCanvasFromTemplate(activeTab)

      const inactiveTab = activeTab === 0 ? 1 : 0
      if (shouldLoadInitialTemplate[inactiveTab === 0 ? 'front' : 'back']) {
        preloadInactiveCanvasJSON(inactiveTab)
      }

      setShouldLoadInitialTemplate((prev) => ({
        ...prev,
        [activeTab === 0 ? 'front' : 'back']: false,
        [inactiveTab === 0 ? 'front' : 'back']: false,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loading, activeTab, isCanvasReady])

  useHotKey('mod+z', async () => {
    await undo()
    await updateTabObject()

    // Check if we reverted back to the clean state
    if (
      stackCursor - 1 === cleanCursor.current[activeTab] &&
      !bsreAutoModified.current[activeTab]
    ) {
      setCanvasModified(activeTab, false)
    } else {
      setCanvasModified(activeTab, true)
    }
  })
  useHotKey('mod+shift+z', async () => {
    await redo()
    await updateTabObject()

    // Check if we advanced to the clean state
    if (
      stackCursor + 1 === cleanCursor.current[activeTab] &&
      !bsreAutoModified.current[activeTab]
    ) {
      setCanvasModified(activeTab, false)
    } else {
      setCanvasModified(activeTab, true)
    }
  })
  useHotKey('mod+c', copy)
  useHotKey('mod+x', cut)
  useHotKey('mod+v', paste)
  useHotKey('mod+d', duplicate)
  useHotKey('delete', () => deleteObject(canvas.current))
  useHotKey('backspace', () => deleteObject(canvas.current))
  useHotKey('mod+arrowup', () => moveActiveObjectUp(canvas.current))
  useHotKey('mod+arrowdown', () => moveActiveObjectDown(canvas.current))
  useHotKey('esc', () => clearAllSelection(canvas.current))

  const initCanvasFromTemplate = async (activeTab: number) => {
    const { frontTemplateUrl, backTemplateUrl } = props.templates || {}

    const currentActiveTabObj = canvasTabObject?.[activeTab]?.canvasObj || {}
    if (currentActiveTabObj?.objects?.length > 0) return

    const ACTIVE_TAB_TEMPLATE_URL: { [key: number]: string | undefined } = {
      0: frontTemplateUrl,
      1: backTemplateUrl,
    }

    const templateUrl = ACTIVE_TAB_TEMPLATE_URL[activeTab] || ''
    if (!templateUrl) return

    const initialTabTemplateString = await getSVGStringFromUrl(
      templateUrl,
      props.variables,
    )

    isInitializingTemplate.current = true

    try {
      if (canvas.current) {
        await loadSVGStringIntoCanvas(initialTabTemplateString, canvas.current)
      }
    } finally {
      isInitializingTemplate.current = false
      canvas.current?.requestRenderAll()
    }
  }

  const preloadInactiveCanvasJSON = async (inactiveTab: number) => {
    const { frontTemplateUrl, backTemplateUrl } = props.templates || {}
    const ACTIVE_TAB_TEMPLATE_URL: { [key: number]: string | undefined } = {
      0: frontTemplateUrl,
      1: backTemplateUrl,
    }

    const templateUrl = ACTIVE_TAB_TEMPLATE_URL[inactiveTab] || ''
    if (!templateUrl) return

    const currentTabObj = canvasTabObject?.[inactiveTab]?.canvasObj || {}
    if (currentTabObj?.objects?.length > 0) return

    try {
      const initialTabTemplateString = await getSVGStringFromUrl(
        templateUrl,
        props.variables,
      )

      const canvasElId = `preload-canvas-${inactiveTab}`
      const canvasEl = document.createElement('canvas')
      canvasEl.id = canvasElId
      canvasEl.style.display = 'none'
      document.body.appendChild(canvasEl)

      const preloadCanvas = createCanvas(canvasElId)
      await loadSVGStringIntoCanvas(initialTabTemplateString, preloadCanvas)

      const wasStampModified = await ensureBsreStampOnCanvas(
        preloadCanvas,
        props.templates?.isUsingTte,
      )

      if (wasStampModified) {
        bsreAutoModified.current[inactiveTab] = true
        setCanvasModified(inactiveTab, true)
      }

      const json = await preloadCanvas.toObject([
        'customId',
        'customVariableKey',
        'customObjectType',
        'customObjectWidth',
        'selectable',
        'evented',
        'lockMovementX',
        'lockMovementY',
        'lockRotation',
        'lockScalingX',
        'lockScalingY',
        'lockScalingFlip',
        'hasControls',
        'hasBorders',
      ])

      canvasTabObject[inactiveTab].canvasObj = json

      preloadCanvas.dispose()
      canvasEl.remove()
    } catch (err) {
      console.warn('Failed to preload inactive canvas:', err)
    }
  }

  const handleActiveTabChange = async (index: number) => {
    await updateTabObject()

    setActiveTab(index)
    await updateTabHistoryStack(activeTab, index)
    // force render to appear last in eventloop queue; probably caused by dom canvas ID change

    deleteCustomObjectFromStackOnTab(activeTab, null, canvas.current)

    setTimeout(async () => {
      canvas.current?.renderAll()
      document.dispatchEvent(certificateTabChangeEvent)
    }, 0)
  }

  const handleSaveOrPublish = async (mode: 'SAVE' | 'PUBLISH' | 'EXPORT') => {
    await handleActiveTabChange(activeTab)

    if (mode === 'PUBLISH') {
      if (
        !props.skipUnsavedChangesGuard &&
        canvasModified.current.some((modified) => modified)
      ) {
        const unsavedPage = canvasModified.current.findIndex(
          (modified) => modified,
        )
          ? 'Halaman Belakang'
          : 'Halaman Depan'
        notification.error({
          description: (
            <>
              Harap simpan perubahan template pada{' '}
              <strong>{unsavedPage}</strong> terlebih dahulu untuk melakukan
              publish
            </>
          ),
          message: `Template Belum Disimpan!`,
        })
        return Promise.resolve()
      }

      const requiredTtePlaceholderCount = props.requiredTtePlaceholderCount || 0
      if (requiredTtePlaceholderCount > 0) {
        const frontCount =
          canvasTabObject[0]?.canvasObj?.objects?.filter(
            (item) => item.customObjectType === TTE_PLACEHOLDER_KEY,
          ).length || 0
        const backCount =
          canvasTabObject[1]?.canvasObj?.objects?.filter(
            (item) => item.customObjectType === TTE_PLACEHOLDER_KEY,
          ).length || 0

        const totalCount = frontCount + backCount

        if (totalCount < requiredTtePlaceholderCount) {
          notification.error({
            message: 'Validasi TTE Placeholder Gagal',
            description: (
              <>
                Template membutuhkan {requiredTtePlaceholderCount} TTE
                Placeholder, namun saat ini baru ada {totalCount}.
              </>
            ),
          })
          return Promise.resolve()
        }

        if (frontCount > 1 || backCount > 1) {
          notification.error({
            message: 'Validasi TTE Placeholder Gagal',
            description:
              'Maksimal 1 TTE Placeholder per halaman. Pastikan placeholder tersebar di halaman depan dan belakang.',
          })
          return Promise.resolve()
        }
      }

      if (props.templates?.isUsingTte) {
        const hasQrCodeFront = canvasTabObject[0]?.canvasObj?.objects?.some(
          (item) => item.customVariableKey === QR_CODE_CERTIFICATE_KEY,
        )
        const hasQrCodeBack = canvasTabObject[1]?.canvasObj?.objects?.some(
          (item) => item.customVariableKey === QR_CODE_CERTIFICATE_KEY,
        )

        if (!hasQrCodeFront && !hasQrCodeBack) {
          notification.error({
            message: 'Komponen Belum Lengkap',
            description: (
              <>
                Anda harus menambahkan setidaknya satu komponen{' '}
                <code>QR_CODE_CERTIFICATE</code> pada template. Tambahkan
                komponen dari tab <strong>Aktor</strong>.
              </>
            ),
          })
          return Promise.resolve()
        }
      }

      try {
        setLoading(true)
        await props.onPublish()
      } catch {
        return Promise.reject()
      } finally {
        setLoading(false)
      }
    }
    const { svgString, usedSystemVariables } = await exportObjectToSVG(
      canvasTabObject[activeTab]?.canvasObj,
    )

    const payload: RAWCertificateTemplatePayload = {
      template: svgString,
      usedSystemVariables,
    }

    if (mode === 'EXPORT') {
      await props.onExport({
        ...payload,
        page: activeTab === 0 ? 'front' : 'back',
      })
      return
    }

    await props.onSave({
      ...payload,
      page: activeTab === 0 ? 'front' : 'back',
    })
    setCanvasModified(activeTab, false)
    bsreAutoModified.current[activeTab] = false
    cleanCursor.current[activeTab] = stackCursor
  }

  const addCustomObjectToDeleteStack = (
    foundObj: any[],
    canvas: Canvas | null,
  ) => {
    setTabObjToDelete((prevStack) => {
      const newStack = prevStack ? [...prevStack] : []
      foundObj.forEach((objs, index) => {
        if (!newStack[index]) {
          newStack[index] = []
        }
        newStack[index] = [...newStack[index], ...objs]
      })

      return newStack
    })
    deleteCustomObjectFromStackOnTab(activeTab, foundObj, canvas)
  }

  const deleteCustomObjectFromStackOnTab = async (
    tabIndex: number,
    foundObj?: any[] | null,
    canvas?: Canvas | null,
  ) => {
    const currentListToDelete =
      foundObj?.[tabIndex] || tabObjToDelete[tabIndex] || []

    if (currentListToDelete.length === 0) return

    const currentCanvasObj = canvas?.getObjects()

    currentListToDelete.forEach((obj: any) => {
      const foundObj = currentCanvasObj?.find(
        (el) => el.customId === obj.customId,
      )
      if (foundObj) {
        canvas?.remove(foundObj)
      }
    })

    // update list to delete
    setTabObjToDelete((prev: any) => {
      const newTabObjToDelete = [...prev]
      newTabObjToDelete[tabIndex] = []
      return newTabObjToDelete
    })

    setTimeout(async () => {
      canvas?.renderAll()
    }, 0)
  }

  const loading = isLoading || props.loading

  return (
    <>
      <ButtonSVGPreview
        onClick={() => generateSVGPreview(canvasTabObject)}
        initialTemplateUrl={{
          front: props.templates?.frontTemplateUrl || '',
          back: props.templates?.backTemplateUrl || '',
        }}
        variables={props.variables}
        shouldLoadInitialTemplate={{
          front: canvasTabObject[0].canvasObj.objects.length === 0,
          back: canvasTabObject[1].canvasObj.objects.length === 0,
        }}
      />
      <div
        ref={container}
        className="flex justify-center items-center p-3 bg-slate-300 fabric-wrapper relative"
        style={{
          zIndex: 2,
          overflow: 'hidden',
        }}
      >
        {loading && (
          <div className="absolute h-full w-full flex items-center justify-center z-40 before:bg-slate-500 before:absolute before:content-[''] before:w-full before:h-full before:opacity-25">
            <Spin size="large" className="z-50" />
          </div>
        )}
        <SelectedComponentConfig canvas={canvas.current} />
        <PageRadio
          onChange={(_, index) => {
            handleActiveTabChange(index)
          }}
          showFrontPageAlert={canvasModifiedState[0]}
          showBackPageAlert={canvasModifiedState[1]}
        />

        {/** Must be placed before canvas element so that the cursor grab styling worked */}
        <CanvasFooter
          canvas={canvas.current}
          targetWrapperId="canvas-wrapper"
        />

        <OrderNavigator
          canvas={canvas.current}
          currentStack={currentStackObj}
          loading={props.navigatorLoading}
        />

        <MainToolbar
          activeTab={activeTab === 0 ? 'front' : 'back'}
          variables={props.variables}
          canvas={canvas.current}
          onCreateVariable={props.onCreateVariable}
          onDeleteVariable={async (key) => {
            const foundObjList = getObjByKey(
              canvasTabObject,
              key,
              props.variables,
            )
            addCustomObjectToDeleteStack(foundObjList, canvas.current)
            await props.onDeleteVariable(key)
          }}
          onExportSVG={() => {
            exportSVG(canvas.current)
          }}
          onAddImage={(result) => {
            if (typeof result !== 'string') {
              message.error('Terdapat gangguan saat menambahkan gambar')
              return
            }
            addImage({
              base64String: result,
              canvas: canvas.current,
            })
          }}
          onAddTextbox={() =>
            addTextbox({
              canvas: canvas.current,
            })
          }
          onPublishClick={async () => {
            await handleSaveOrPublish('PUBLISH')
          }}
          onSaveClick={async () => await handleSaveOrPublish('SAVE')}
          onExportClick={async () => await handleSaveOrPublish('EXPORT')}
          publishLabel={props.publishLabel}
        />

        <div
          id="canvas-wrapper"
          style={{
            width: '297mm',
            height: '210mm',
            background: 'white',
            zIndex: 1,
          }}
          className="peer-[.canvas-grab]:*:*:!cursor-grab peer-[.canvas-grabbing]:*:*:!cursor-grabbing"
        >
          <canvas id={CANVAS_TAB_ID[activeTab]} />
        </div>
      </div>
    </>
  )
}

export default FabricWrapper
